// homeLook.js — THE PERSON'S OWN LOOK OF THE HOME TOP
// (DESIGN-WL-HOME-TOP-STRIP-SUMMARY-1; Nika's picks 2026-09-28, rows 1, 2, 4:
// the App strip as a Dock OR a Rail, the summary as a Brief OR Signals, one
// grouped Personalise panel with a per-user background).
//
// ONE bag on the user record, `user_preferences.homeLook`:
//   stripShown    boolean                the App strip on / off
//   stripStyle    'dock' | 'rail'        one glass capsule above the board, or
//                                        a slim rail beside it
//   stripLabels   boolean                app names under the glyphs (off = the
//                                        name on hover and focus)
//   navbarIcons   boolean                the navbar tabs as glyph plates, each
//                                        name in the house tooltip (Nika
//                                        2026-10-23: "try changing text navbar
//                                        items with icons" — default ON); off =
//                                        the text tabs (2026-10-02: optional)
//   navbarPosition      'top' | 'left' |  where the workspace host's navbar
//                       'right' | 'bottom' sits (Nika 2026-10-24: "make sidebar
//                                        optional to be positioned at the top
//                                        left right or bottom") — default
//                                        'left' (Nika 2026-10-27: "also make
//                                        navbar left by default"; it was 'top',
//                                        2026-10-24 "for normal workspace keep
//                                        as is")
//   navbarExpanded      boolean          a left / right rail shows the names
//                                        beside the glyphs ("left and right
//                                        sidebars can be toggle expandable")
//   commNavbarPosition  the same four    the comm host's own ("for com - move
//                                        to the left") — default 'left'
//   commNavbarExpanded  boolean          the comm host's rail, expanded
//   summaryStyle  'brief' | 'signals'    two calm sentences, or four tiles
//   sky           a sky of the files plane ('clouds', 'dunes', … — whatever
//                 files/ambient holds), 'none', or null = follow the
//                 workspace's appearance
//   skyLayout     'top' | 'bottom'       where the ambient sky sits on the
//                                        home (DESIGN-WL-HOME-SKY-TOP-GROUND-1,
//                                        Nika 2026-10-01: "clouds and mountain
//                                        should be the default", "keep bottom
//                                        moving clouds as one of the options"):
//                                        'top' = the clouds hang from the top
//                                        and the mountain ground rises at the
//                                        end of the home scroll (the default);
//                                        'bottom' = the clouds in the bottom
//                                        band, no ground (the layout before)
// A PERSON's choice, like `themeMode` beside it on the same row: it follows
// them to every workspace and every device. An explicit sky wins over the
// workspace's own (functions/ambientBackdrop.js `resolveSky`).
//
// PRECEDENCE — the record wins at boot:
//   1. this session's decision (`root._homeLook`, written by homeLookSet) — it
//      is newer than any read, including a record that lands after it;
//   2. the record (`root.userPrefs.homeLook`) once the row has loaded;
//   3. this device's copy (localStorage `symbols_home_look`) — it paints before
//      the record arrives and mirrors the record once it has (homeLookSyncCache);
//      every tab of the device shares it, so each write is stamped
//      (`symbols_home_look_at`) and no tab writes an older look over it;
//      like the record and the session look it holds the person's EXPLICIT
//      choices only, never a default, so a changed default reaches everybody
//      who never chose that key;
//   4. the defaults: Nika's recommended picks — the strip shown as a Dock with
//      the names on hover, the Brief, the workspace's own sky, the sky at
//      the top with the mountain ground.
//
// WRITES ARE DECISIONS (the home-prefs durability law): at once, never
// debounced, and SINGLE-FLIGHT on the queue every writer of the
// user_preferences row already shares (`root.__navbarSaveQueue` — the navbar
// tabs, the assistant starters): one request at a time, the payload composed
// at SEND time from the live row (the update REPLACES the row, so a payload
// built at click time would drop what another writer landed meanwhile), the
// newest decision lands last. A row that has not loaded is read first — a
// write never replaces the account's other preferences with one key. The
// response is adopted only when no newer decision is waiting, so Dock → Rail →
// Dock never flickers back through Rail.
//
// FRANK: every export is self-contained (FA204) — the vocabularies and the
// defaults are restated where they are used; siblings are reached through
// `this.call`.

// The record bag, the device copy, then the defaults, one key at a time; only
// valid values pass. Pure: `homeLookNormalize(bag)` → the valid keys only.
export const homeLookNormalize = function homeLookNormalize(bag) {
  const out = {}
  if (!bag || typeof bag !== 'object') return out
  if (typeof bag.stripShown === 'boolean') out.stripShown = bag.stripShown
  if (bag.stripStyle === 'dock' || bag.stripStyle === 'rail') out.stripStyle = bag.stripStyle
  if (typeof bag.stripLabels === 'boolean') out.stripLabels = bag.stripLabels
  if (typeof bag.navbarIcons === 'boolean') out.navbarIcons = bag.navbarIcons
  for (const k of ['navbarPosition', 'commNavbarPosition']) {
    if (['top', 'left', 'right', 'bottom'].includes(bag[k])) out[k] = bag[k]
  }
  for (const k of ['navbarExpanded', 'commNavbarExpanded']) {
    if (typeof bag[k] === 'boolean') out[k] = bag[k]
  }
  if (bag.summaryStyle === 'brief' || bag.summaryStyle === 'signals') {
    out.summaryStyle = bag.summaryStyle
  }
  if (bag.sky === null || (typeof bag.sky === 'string' && /^[a-z][a-z0-9-]{0,31}$/.test(bag.sky))) {
    out.sky = bag.sky
  }
  if (bag.skyLayout === 'top' || bag.skyLayout === 'bottom') out.skyLayout = bag.skyLayout
  // THE PAGE SIDEBAR'S CHOICE (shared functions/pageSidebar.js): page → hidden.
  // At most 64 pages, each a valid key with a boolean.
  const sh = bag.sidebarHidden
  if (sh && typeof sh === 'object' && !Array.isArray(sh)) {
    const map = {}
    for (const k of Object.keys(sh).slice(0, 64)) {
      if (/^[a-z0-9][a-z0-9_-]{0,63}$/.test(k) && typeof sh[k] === 'boolean') map[k] = sh[k]
    }
    if (Object.keys(map).length) out.sidebarHidden = map
  }
  return out
}

// This device's copy, or null. `storage` is a test seam.
export const readStoredHomeLook = function readStoredHomeLook(storage) {
  try {
    const store = storage || (typeof localStorage !== 'undefined' ? localStorage : null)
    const raw = store ? store.getItem('symbols_home_look') : null
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch (_) {
    return null
  }
}

// The effective look → { stripShown, stripStyle, stripLabels, summaryStyle,
// sky, skyLayout } (`sky: null` = the workspace's own; `skyLayout` 'top'
// unless the person chose 'bottom'). Reactive: a caller's effect
// subscribes to `_homeLook` and `userPrefs` — both keys are read on every
// call, whichever wins.
export const homeLookRead = function homeLookRead(root, storage) {
  const norm = (bag) => {
    const out = {}
    if (!bag || typeof bag !== 'object') return out
    if (typeof bag.stripShown === 'boolean') out.stripShown = bag.stripShown
    if (bag.stripStyle === 'dock' || bag.stripStyle === 'rail') out.stripStyle = bag.stripStyle
    if (typeof bag.stripLabels === 'boolean') out.stripLabels = bag.stripLabels
    if (typeof bag.navbarIcons === 'boolean') out.navbarIcons = bag.navbarIcons
    for (const k of ['navbarPosition', 'commNavbarPosition']) {
      if (['top', 'left', 'right', 'bottom'].includes(bag[k])) out[k] = bag[k]
    }
    for (const k of ['navbarExpanded', 'commNavbarExpanded']) {
      if (typeof bag[k] === 'boolean') out[k] = bag[k]
    }
    if (bag.summaryStyle === 'brief' || bag.summaryStyle === 'signals') {
      out.summaryStyle = bag.summaryStyle
    }
    if (
      bag.sky === null ||
      (typeof bag.sky === 'string' && /^[a-z][a-z0-9-]{0,31}$/.test(bag.sky))
    ) {
      out.sky = bag.sky
    }
    if (bag.skyLayout === 'top' || bag.skyLayout === 'bottom') out.skyLayout = bag.skyLayout
    // THE PAGE SIDEBAR'S CHOICE (shared functions/pageSidebar.js): page → hidden.
    // At most 64 pages, each a valid key with a boolean.
    const sh = bag.sidebarHidden
    if (sh && typeof sh === 'object' && !Array.isArray(sh)) {
      const map = {}
      for (const k of Object.keys(sh).slice(0, 64)) {
        if (/^[a-z0-9][a-z0-9_-]{0,63}$/.test(k) && typeof sh[k] === 'boolean') map[k] = sh[k]
      }
      if (Object.keys(map).length) out.sidebarHidden = map
    }
    return out
  }
  const r = root && typeof root === 'object' ? root : {}
  const session = r._homeLook
  const prefs = r.userPrefs
  const record = prefs && typeof prefs === 'object' ? norm(prefs.homeLook) : null
  const mine = session && typeof session === 'object' ? norm(session) : null
  let device = null
  if (!mine && !record) {
    try {
      const store = storage || (typeof localStorage !== 'undefined' ? localStorage : null)
      const raw = store ? store.getItem('symbols_home_look') : null
      device = raw ? norm(JSON.parse(raw)) : null
    } catch (_) {
      device = null
    }
  }
  return {
    stripShown: true,
    stripStyle: 'dock',
    stripLabels: false,
    navbarIcons: true,
    navbarPosition: 'left',
    navbarExpanded: false,
    commNavbarPosition: 'left',
    commNavbarExpanded: false,
    summaryStyle: 'brief',
    sky: null,
    skyLayout: 'top',
    ...(device || {}),
    ...(record || {}),
    ...(mine || {}),
    // A MAP, merged page by page (shared functions/pageSidebar.js): a page this
    // session decided wins; every other page keeps the record's choice (a
    // second device's choice lands with the next row).
    sidebarHidden: {
      ...((device && device.sidebarHidden) || {}),
      ...((record && record.sidebarHidden) || {}),
      ...((mine && mine.sidebarHidden) || {})
    }
  }
}

// A decision: `el.call('homeLookSet', { stripStyle: 'rail' })`. Writes the
// session look (the board behind the panel re-renders at once), this device's
// copy, the mirrored row when it has loaded, then the record through the
// shared single-flight queue. Returns the write's promise (tests await it).
// `opts`: { root, sdk, storage, clock } — test seams; the element supplies them
// live (`clock`: { now(), bootAt } — see homeLookSyncCache).
export const homeLookSet = function homeLookSet(patch, opts) {
  const norm = (bag) => {
    const out = {}
    if (!bag || typeof bag !== 'object') return out
    if (typeof bag.stripShown === 'boolean') out.stripShown = bag.stripShown
    if (bag.stripStyle === 'dock' || bag.stripStyle === 'rail') out.stripStyle = bag.stripStyle
    if (typeof bag.stripLabels === 'boolean') out.stripLabels = bag.stripLabels
    if (typeof bag.navbarIcons === 'boolean') out.navbarIcons = bag.navbarIcons
    for (const k of ['navbarPosition', 'commNavbarPosition']) {
      if (['top', 'left', 'right', 'bottom'].includes(bag[k])) out[k] = bag[k]
    }
    for (const k of ['navbarExpanded', 'commNavbarExpanded']) {
      if (typeof bag[k] === 'boolean') out[k] = bag[k]
    }
    if (bag.summaryStyle === 'brief' || bag.summaryStyle === 'signals') {
      out.summaryStyle = bag.summaryStyle
    }
    if (
      bag.sky === null ||
      (typeof bag.sky === 'string' && /^[a-z][a-z0-9-]{0,31}$/.test(bag.sky))
    ) {
      out.sky = bag.sky
    }
    if (bag.skyLayout === 'top' || bag.skyLayout === 'bottom') out.skyLayout = bag.skyLayout
    // THE PAGE SIDEBAR'S CHOICE (shared functions/pageSidebar.js): page → hidden.
    // At most 64 pages, each a valid key with a boolean.
    const sh = bag.sidebarHidden
    if (sh && typeof sh === 'object' && !Array.isArray(sh)) {
      const map = {}
      for (const k of Object.keys(sh).slice(0, 64)) {
        if (/^[a-z0-9][a-z0-9_-]{0,63}$/.test(k) && typeof sh[k] === 'boolean') map[k] = sh[k]
      }
      if (Object.keys(map).length) out.sidebarHidden = map
    }
    return out
  }
  const el = this && typeof this === 'object' ? this : null
  const o = opts && typeof opts === 'object' ? opts : {}
  const root = o.root || (el && el.state && el.state.root) || null
  const sdk = o.sdk || (el && el.sdk) || null
  const change = norm(patch)
  if (!root || typeof root.update !== 'function' || !Object.keys(change).length) {
    return Promise.resolve(null)
  }
  const prefs = root.userPrefs && typeof root.userPrefs === 'object' ? root.userPrefs : null
  // The person's EXPLICIT choices so far — never the effective look: a
  // missing key never becomes a stored value, so a changed default still
  // reaches everybody who never chose that key
  // (DESIGN-WL-HOME-DEFAULTS-ALL-WORKSPACES-1). Same order as the read:
  // this session's decisions, else the record once its row has loaded (a
  // legacy device copy full of defaults heals here), else this device's copy.
  let chosen = null
  if (root._homeLook && typeof root._homeLook === 'object') chosen = norm(root._homeLook)
  else if (prefs) chosen = norm(prefs.homeLook)
  else {
    try {
      const store = o.storage || (typeof localStorage !== 'undefined' ? localStorage : null)
      const raw = store ? store.getItem('symbols_home_look') : null
      chosen = raw ? norm(JSON.parse(raw)) : null
    } catch (_) {
      chosen = null
    }
  }
  const next = { ...(chosen || {}), ...change }
  // The page-sidebar map changes PAGE BY PAGE: a decision for one page keeps
  // every other page's choice (shared functions/pageSidebar.js).
  const pages = (bag) => (bag && bag.sidebarHidden) || {}
  if (change.sidebarHidden) {
    next.sidebarHidden = { ...pages(chosen), ...change.sidebarHidden }
  }
  const patchState = { _homeLook: next }
  if (prefs) {
    const was = norm(prefs.homeLook)
    const homeLook = { ...was, ...change }
    if (change.sidebarHidden) {
      homeLook.sidebarHidden = { ...pages(was), ...change.sidebarHidden }
    }
    patchState.userPrefs = { ...prefs, homeLook }
  }
  root.update(patchState, { preventFetch: true })
  // THIS DEVICE'S COPY — READ BEFORE THE WRITE (N3, Nika 2026-10-27: an old
  // tab put back a navbar place the person had changed since). Every tab of
  // this device shares the copy, and this tab's base is as old as its
  // record: when the copy was written AFTER that record could have landed
  // (its stamp `symbols_home_look_at` against this tab's own mark — the note
  // at homeLookSyncCache), another tab wrote it, so the decision lands ON
  // that copy and only the decided keys change. Otherwise this tab's base
  // stands (a legacy copy full of defaults heals here, as before). The
  // record is the source of truth either way: this is its paint-ahead copy.
  const clock = o.clock && typeof o.clock === 'object' ? o.clock : null
  const now = clock && typeof clock.now === 'function' ? clock.now() : Date.now()
  let mem = root.__homeLookCache
  if (!mem || typeof mem !== 'object') {
    const bootAt =
      clock && typeof clock.bootAt === 'number'
        ? clock.bootAt
        : typeof performance !== 'undefined' && performance && performance.timeOrigin
          ? performance.timeOrigin
          : now
    mem = root.__homeLookCache = { mirrored: null, recordAt: bootAt, lookedAt: bootAt }
  }
  try {
    const store = o.storage || (typeof localStorage !== 'undefined' ? localStorage : null)
    if (store) {
      const at = Number(store.getItem('symbols_home_look_at')) || 0
      let base = chosen || {}
      if (at > mem.recordAt) {
        const raw = store.getItem('symbols_home_look')
        base = raw ? norm(JSON.parse(raw)) : {}
      }
      const copy = { ...base, ...change }
      if (change.sidebarHidden) {
        copy.sidebarHidden = { ...pages(base), ...change.sidebarHidden }
      }
      store.setItem('symbols_home_look', JSON.stringify(copy))
      store.setItem('symbols_home_look_at', String(now))
    }
  } catch (err) {
    console.warn('[home] look cache write failed:', err && err.message)
  }
  // This tab's own write is no news for the mirror: the record it just
  // patched is the one it composed, never a row from the server.
  if (patchState.userPrefs) mem.mirrored = JSON.stringify(patchState.userPrefs.homeLook)
  if (!sdk || typeof sdk.execute !== 'function') return Promise.resolve(null)
  // The queue every user_preferences writer shares, keyed as they key it.
  const w = root.workspace
  const key =
    String(
      (w && (w.id || w._id)) || (root.jwtClaims && root.jwtClaims.active_workspace_id) || ''
    ) || 'user'
  if (!root.__navbarSaveQueue) root.__navbarSaveQueue = new Map()
  const queue = root.__navbarSaveQueue
  root.__homeLookSeq = (root.__homeLookSeq || 0) + 1
  const seq = root.__homeLookSeq
  const prior = queue.get(key) || Promise.resolve()
  const task = prior
    .catch(() => {})
    .then(async () => {
      // PER-PATH PATCH — one path per CHANGED homeLook key, so neither
      // another top-level preference nor a DIFFERENT homeLook key a second
      // tab wrote is overwritten (CORE-USERPREFS-PATCH-PER-PATH-1).
      const row = await sdk.execute('workspaceProject.userPreferences', 'patch', {
        patch: {
          // …and the page-sidebar map one path per PAGE, so another tab's
          // choice for another page is never overwritten.
          set: Object.entries(change).flatMap(([k, v]) =>
            k === 'sidebarHidden'
              ? Object.entries(v).map(([page, hide]) => ({
                  path: ['homeLook', 'sidebarHidden', page],
                  value: hide
                }))
              : [{ path: ['homeLook', k], value: v }]
          )
        }
      })
      // Adopt the confirmed row only when no newer decision is waiting.
      if (seq === root.__homeLookSeq && row && typeof row === 'object') {
        const now = root.userPrefs && typeof root.userPrefs === 'object' ? root.userPrefs : null
        root.update(
          {
            userPrefs: now
              ? { ...now, homeLook: row.homeLook || { ...norm(now.homeLook), ...change } }
              : row
          },
          { preventFetch: true }
        )
      }
      return row
    })
  queue.set(key, task)
  return task.catch((err) => {
    console.warn('[home] look save failed:', err && err.message)
    return null
  })
}

// Mirror the record into this device's copy once it has loaded, so the NEXT
// boot on this device paints the person's look before the record arrives.
// Lifecycle only (the greeting head's render and its clock) — never a render
// prop. Returns true when the copy changed.
//
// EVERY TAB OF THIS DEVICE SHARES THE COPY, and a tab's record is as old as
// the moment it landed. This mirror used to write the tab's record on every
// call, so a tab loaded hours earlier put its old look back over the one the
// person had picked since in another tab — every 30 s, on the head's clock
// (N3, Nika 2026-10-27: the stale navbar place). Now the copy moves only on
// news, and never back:
//   - news: this tab's record changed since its last mirror — a row landed
//     (the boot read, a workspace switch, a confirmed write that another tab
//     or device changed). The same record again is no news, however often
//     the clock asks; this tab's own decision is no news either (homeLookSet
//     marks it).
//   - never back: every write stamps the copy (`symbols_home_look_at`, this
//     device's clock), and the tab keeps its mark `root.__homeLookCache`:
//     `recordAt`, the earliest moment its record can have landed (its boot
//     for the first row, its previous look for a later one). A copy stamped
//     after that may hold a newer pick, so it stays.
// `storage` and `clock` ({ now(), bootAt }) are test seams.
export const homeLookSyncCache = function homeLookSyncCache(root, storage, clock) {
  const r = root && typeof root === 'object' ? root : null
  if (!r) return false
  const c = clock && typeof clock === 'object' ? clock : null
  const now = c && typeof c.now === 'function' ? c.now() : Date.now()
  let mem = r.__homeLookCache
  if (!mem || typeof mem !== 'object') {
    const bootAt =
      c && typeof c.bootAt === 'number'
        ? c.bootAt
        : typeof performance !== 'undefined' && performance && performance.timeOrigin
          ? performance.timeOrigin
          : now
    mem = r.__homeLookCache = { mirrored: null, recordAt: bootAt, lookedAt: bootAt }
  }
  const lookedAt = mem.lookedAt
  mem.lookedAt = now
  const prefs = r.userPrefs
  const bag = prefs && typeof prefs === 'object' ? prefs.homeLook : null
  if (!bag || typeof bag !== 'object') return false
  const next = JSON.stringify(bag)
  if (mem.mirrored === next) return false
  mem.mirrored = next
  mem.recordAt = lookedAt
  try {
    const store = storage || (typeof localStorage !== 'undefined' ? localStorage : null)
    if (!store) return false
    const at = Number(store.getItem('symbols_home_look_at')) || 0
    if (at > mem.recordAt) return false
    if (store.getItem('symbols_home_look') === next) return false
    store.setItem('symbols_home_look', next)
    store.setItem('symbols_home_look_at', String(now))
    return true
  } catch (_) {
    return false
  }
}

// The Theme row of the Personalise panel — the SAME explicit-theme path the
// account menu takes (shared accountMenu.js 'themeMode'): the root mode, this
// device's copy, the framework's theme (never a data-theme write) and the user
// record. `this` is the segment the person picked (AppSegmented `select`).
export const homeLookSetTheme = function homeLookSetTheme(mode) {
  const el = this
  const root = el && el.state && el.state.root
  if (!root || typeof root.update !== 'function' || typeof el.call !== 'function') return
  const next = el.call('normalizeThemeMode', mode)
  root.update({ themeMode: next }, { preventFetch: true })
  el.call('writeStoredThemeMode', next)
  el.call('changeGlobalTheme', next)
  el.call('persistThemeModeToUser', root, el.sdk, next)
}

// The segmented rows' writers (AppSegmented `select` names a function): the
// strip style and the summary style.
export const homeLookSetStripStyle = function homeLookSetStripStyle(value) {
  if (this && typeof this.call === 'function')
    return this.call('homeLookSet', { stripStyle: value })
}

// THE APP STRIP's look gate — the ONE place the person's look hides the Dock
// (style 'dock') or the Rail (style 'rail'): the strip turned off hides both;
// the Rail shows only in the Rail style (the Dock row stays mounted there —
// its own `@aboveMobileL` rule steps it aside, the phone keeps it). Called by
// the components' own `hide` (components/HomeAppStrip.js, HomeAppRail.js) and
// by the page's mount-site hide (pages/main.js `_appStripHidden`, which adds
// "no app left to show") — a mount-site function replaces the component's,
// so both read this instead of restating it.
export const homeAppStripLookHidden = function homeAppStripLookHidden(root, style) {
  const look =
    (this && typeof this.call === 'function' ? this.call('homeLookRead', root) : null) || {}
  if (look.stripShown === false) return true
  return style === 'rail' && look.stripStyle !== 'rail'
}

// The Background group's layout row (AppSegmented `select`): 'top' — the
// clouds at the top and the mountain ground at the end of the scroll — or
// 'bottom', the clouds in the bottom band as before.
export const homeLookSetSkyLayout = function homeLookSetSkyLayout(value) {
  if (this && typeof this.call === 'function') return this.call('homeLookSet', { skyLayout: value })
}

export const homeLookSetSummaryStyle = function homeLookSetSummaryStyle(value) {
  if (this && typeof this.call === 'function') {
    return this.call('homeLookSet', { summaryStyle: value })
  }
}

// The Personalise panel's app list: move one app of the App strip before or
// after another, through the board's own layout ops (the ORDER lives in the
// home layout, per workspace — pages/main.js `_homeOps`), and persist at once.
// `homeLayoutMove` semantics: moving onto an EARLIER app lands before it,
// onto a LATER app lands after it.
export const homeLookMoveApp = function homeLookMoveApp(id, targetId) {
  const el = this
  const root = el && el.state && el.state.root
  const ops = root && root._homeOps
  if (!ops || typeof ops.moveWidget !== 'function' || !id || !targetId || id === targetId) {
    return false
  }
  ops.moveWidget(id, targetId)
  if (typeof ops.persistLayout === 'function') ops.persistLayout()
  return true
}

// The Background group's chosen sky: the person's own, else the one the
// workspace shows (its record, the server's resolved sky, the default) —
// 'none' when the workspace itself chose no sky. `s` is a sky tile's state
// (its `.root` the shell root).
export const homeSkyActive = function homeSkyActive(s) {
  const el = this
  if (!el || typeof el.call !== 'function') return null
  const root = s && s.root
  const look = el.call('homeLookRead', root)
  if (look && look.sky) return look.sky
  const state = el.call('ambientSkyState', s)
  if (state && state.sky) return state.sky
  return state && state.reason === 'sky-none' ? 'none' : null
}

// The Background group's choices: every sky the ambient files index holds (the
// files plane — `ambient/<sky>.png`, files/ambient/index.js), in its order,
// then None. A sky added to the index (the loader lane's `mountains`) is
// listed with no change here; its name is a bundle key from the table below,
// and a sky with no key yet shows its own id (polyglot returns a non-key as it
// is) rather than no name at all.
export const homeSkyChoices = function homeSkyChoices() {
  const el = this
  const names = {
    clouds: 'workspace.personalize.skyClouds',
    dunes: 'workspace.personalize.skyDunes',
    hills: 'workspace.personalize.skyHills',
    lake: 'workspace.personalize.skyLake',
    window: 'workspace.personalize.skyWindow',
    mountains: 'workspace.personalize.skyMountains'
  }
  const files =
    el && el.context && el.context.files && typeof el.context.files === 'object'
      ? el.context.files
      : {}
  const skies = []
  for (const key of Object.keys(files)) {
    const m = /^(?:\/files\/)?ambient\/([a-z][a-z0-9-]{0,31})\.png$/.exec(key)
    if (m && !skies.includes(m[1])) skies.push(m[1])
  }
  const rows = skies.map((sky) => ({ key: sky, value: sky, label: names[sky] || sky }))
  rows.push({ key: 'none', value: 'none', label: 'workspace.personalize.skyNone' })
  return rows
}
